import axios from 'axios';
import { supabase } from '../supabase';

// REACT_APP_API_BASE_URL should be set in the deployed frontend's
// environment (e.g. Railway/Vercel env vars) to the deployed backend's
// URL. Falls back to localhost for local development so nothing breaks
// for existing dev workflows.
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const API = axios.create({
  baseURL: API_BASE_URL
});

// New (Aug 31, per Ali's report) — the other required half of tonight's
// backend auth fix (see app/auth.py). Every protected backend route now
// verifies a real Supabase-issued JWT on every request instead of
// blindly trusting whatever user_id was sent in the body — but that
// only works if the frontend actually attaches one. Before this, NO
// request anywhere in this file sent an Authorization header at all;
// every one of those newly-protected routes would otherwise start
// returning 401 for every real user the moment the backend change
// deployed, not just for attackers.
//
// getSession() reads the current session from local storage — cheap,
// no network call — and is the same session object Supabase's own
// onAuthStateChange already keeps fresh via automatic token refresh,
// so this always attaches a current, valid token without this file
// needing to manage refresh logic itself. Silently proceeds without a
// header if there's no session (e.g. a genuinely public/pre-login
// call) — the backend's own get_current_user_id is what actually
// enforces "this route requires a real token," not this interceptor.
API.interceptors.request.use(async (config) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) {
      config.headers.Authorization = `Bearer ${session.access_token}`;
    }
  } catch {
    // Best-effort — a failed session lookup just means this request
    // goes out unauthenticated, same as if no session existed at all;
    // the backend correctly rejects it rather than this interceptor
    // needing to guess at error handling here.
  }
  return config;
});

// Suggests a currency based on the visitor's IP, for pre-filling the
// pin-creation currency dropdown. Always resolves (never throws) — a
// failed/unavailable lookup returns { currency: null }, and PinTrip.js
// falls back to its own browser-locale guess in that case, same as if
// this call was never made.
// Live currency list for the pin-creation dropdown — sourced from the
// same backend endpoint (and ultimately the same provider) actually
// used for conversion, so the dropdown can't offer a currency the
// backend later fails to convert. Falls back to null on any failure;
// PinTrip.js keeps its small hardcoded list as the fallback in that case.
export const getSupportedCurrencies = async () => {
  try {
    const response = await API.get('/geo/supported-currencies');
    return response.data.currencies;
  } catch (error) {
    return null;
  }
};

export const getSuggestedCurrency = async () => {
  try {
    const response = await API.get('/geo/suggest-currency');
    return response.data;
  } catch (error) {
    return { currency: null, country_code: null, source: 'none' };
  }
};

// Referral program — mutual reward (both sides get a bonus pin slot),
// see growth.py for why this shape was chosen over a cash/points system.
export const getReferralCode = async (userId) => {
  const response = await API.get(`/growth/${userId}/referral-code`);
  return response.data;
};

// Identical read calls made at the same moment share one network request
// (the Dashboard and its child components used to ask for the same limits /
// freezes twice). No caching beyond the request itself, so data is never stale.
const _inflightGets = new Map();
const dedupeGet = (key, fn) => {
  if (_inflightGets.has(key)) return _inflightGets.get(key);
  const p = fn().finally(() => _inflightGets.delete(key));
  _inflightGets.set(key, p);
  return p;
};

export const getReferralStats = (userId) => dedupeGet(`/growth/${userId}/referral-stats`, async () => {
  const response = await API.get(`/growth/${userId}/referral-stats`);
  return response.data;
});

// Called once after a fresh login if a pending referral code was
// captured at signup — never throws on an "already applied" or
// "invalid code" outcome, since neither is a real error worth
// interrupting login over.
export const applyReferralCode = async (userId, referralCode) => {
  try {
    const response = await API.post(`/growth/${userId}/apply-referral`, { referral_code: referralCode });
    return response.data;
  } catch (error) {
    return { status: 'error' };
  }
};

// Achievements — computed live on the backend from real pin data, see
// growth.py's _compute_achievements for exactly what each badge means.
export const getAchievements = (userId) => dedupeGet(`/growth/${userId}/achievements`, async () => {
  const response = await API.get(`/growth/${userId}/achievements`);
  return response.data;
});

export const pinTrip = async (tripData) => {
  const response = await API.post('/trips/pin', tripData);
  return response.data;
};

export const editPin = async (oldPinId, tripData) => {
  const response = await API.put(`/trips/${oldPinId}/edit`, tripData);
  return response.data;
};

// Hotel pin equivalents — separate table, separate tier cap (Premium
// only), separate scheduler job on the backend, so these mirror the
// trip functions above rather than sharing an endpoint.
export const pinHotel = async (hotelData) => {
  const response = await API.post('/hotels/pin', hotelData);
  return response.data;
};

export const editHotelPin = async (oldPinId, hotelData) => {
  const response = await API.put(`/hotels/${oldPinId}/edit`, hotelData);
  return response.data;
};

export const deleteHotelPin = async (pinId) => {
  const response = await API.delete(`/hotels/${pinId}`);
  return response.data;
};

export const toggleHotelUrgent = async (pinId, mark) => {
  const response = await API.post(`/hotels/${pinId}/urgent`, { mark });
  return response.data;
};

// Creates a Duffel Links hosted booking session and returns the URL to
// redirect the user to. Note: this opens a fresh search on Duffel's own
// inventory (same source our price-checker uses), not a pre-selected
// deep link to the exact prior offer — Links doesn't support passing a
// specific offer_id.
export const createBookingLink = async (userId, pinId) => {
  const response = await API.post('/links/create-session', { user_id: userId, pin_id: pinId });
  return response.data;
};

// Powers the "pins used / limit" dashboard stat — the frontend has
// never had visibility into a user's own tier before this.
export const getTierLimits = (userId) => dedupeGet(`/trips/limits/${userId}`, async () => {
  const response = await API.get(`/trips/limits/${userId}`);
  return response.data;
});

export const markOnboardingSeen = async (userId) => {
  const response = await API.post(`/trips/onboarding-seen/${userId}`);
  return response.data;
};

// Real scheduler state — replaces the old frontend-side JS guess at
// "which pin is next" with the actual next_run_time from the backend's
// scheduler, plus whichever of this user's pins are genuinely due right
// now (and will therefore be checked at that next tick together).
export const getNextCheck = async (userId) => {
  const response = await API.get(`/trips/next-check/${userId}`);
  return response.data;
};

// searchFlights removed (Sep 5, per Ali's report — real QA-checklist
// finding): confirmed via a full frontend search that no page ever
// called this. It wrapped GET /flights/search, itself removed on the
// backend the same day for being live, unauthenticated, dead code —
// see routes/flights.py's own comment for the full reasoning.

// Re-runs the same Duffel search that produced the deal on-screen, right
// at the moment the user clicks Book Now, so the price they see next
// (before we send them to Skyscanner/Booking.com) reflects
// right now rather than whenever price_checker last ran in the
// background — which could be stale by minutes or hours.
export const verifyPrice = async (payload) => {
  const response = await API.post('/flights/verify-price', payload);
  return response.data;
};

export const getPins = async (userId) => {
  const response = await API.get(`/trips/pins/${userId}`);
  return response.data;
};

// New (Sep 5, per Ali's report) — powers the Dashboard's collapsible
// Trip History section. Deliberately a separate call, not folded into
// getPins above — history is fetched lazily, only when someone actually
// expands the section, not on every Dashboard load.
// "Share this deal" (Oct 2026): a public link / PDF page / email-to-self for
// this pin's current fare. Built on the server, see backend app/routes/share.py.
export const createShareLink = async (pinId) => {
  const response = await API.post('/share/pin', { pin_id: pinId });
  return response.data;
};

export const emailShare = async (pinId) => {
  const response = await API.post('/share/email/pin', { pin_id: pinId });
  return response.data;
};

export const getPinHistory = async (userId) => {
  const response = await API.get(`/trips/history/${userId}`);
  return response.data;
};

// Mirrors getPins above, hitting the hotels router instead — hotel pins
// live in their own table with their own tier cap, entirely independent
// from trip_pins, so this is a separate endpoint, not a shared one.
export const getHotelPins = async (userId) => {
  const response = await API.get(`/hotels/pins/${userId}`);
  return response.data;
};

export const deletePin = async (pinId) => {
  const response = await API.delete(`/trips/${pinId}`);
  return response.data;
};

// Reactivates a deactivated pin (Aug 23, per Ali's report — part of the
// Phase A tiered check-count system). Mirrors deletePin's shape exactly.
// The backend refuses with a real error if the pin has permanently
// exhausted its lifetime check cap — this call can fail on purpose,
// callers should surface that message rather than assume success.
//
// dates param added (Sep 20) — optional {departure_date, return_date},
// required by the backend only when the pin's deactivation_reason is
// 'departure_passed' (see reactivate_pin's own docstring in
// routes/trips.py). Omitted entirely for the lifetime_limit/deleted
// cases, which still reactivate with no body, unchanged.
export const reactivatePin = async (pinId, dates = null) => {
  const response = await API.post(`/trips/${pinId}/reactivate`, dates || {});
  return response.data;
};

// Resume a pin paused for inactivity (Sep 23) — distinct from
// reactivatePin above, which is for a DEPARTED pin and requires new
// dates. This one asks nothing further: same trip, same price
// history, same remaining checks, picking up exactly where it paused.
export const resumePausedPin = async (pinId) => {
  const response = await API.post(`/trips/${pinId}/resume`);
  return response.data;
};

// The free, one-per-account 24h Price Watch trial (Sep 24) — no
// duration choice, no payment step, so this is a direct create call
// rather than the two-step duration-picker + payment-modal flow the
// paid tiers use. Eligibility (has this account used its trial yet)
// comes from getTierLimits' price_watch_trial_available, not from
// this call — the pitch card only shows the trial button when that's
// already true, and the backend re-checks it independently anyway.
export const startTrialWatch = async (pinId, userId) => {
  const response = await API.post('/freezes/trial', { pin_id: pinId, user_id: userId });
  return response.data;
};

// One-time admin cleanup (Sep 24) — fixes pins whose watch resolved
// before the unconditional-retirement fix shipped. Backend-enforced
// founder-only (require_founder); calling it as anyone else returns a
// 403. Uses the app's own already-authenticated session, so there's
// no manual token to extract — just needs the founder to actually be
// signed in when they click it.
export const fixStuckRetirements = async () => {
  const response = await API.post('/freezes/admin/fix-stuck-retirements');
  return response.data;
};

export const saveDeviceToken = async (userId, deviceToken) => {
  const response = await API.patch(`/users/${userId}/device-token`, {
    device_token: deviceToken
  });
  return response.data;
};

// Account settings (Sep 7, per Ali's report — Phase 2). Notification
// preferences follow the same partial-update convention as the rest
// of this file's PATCH calls — pass only the field(s) actually
// changing, not the full object every time.
export const getNotificationPreferences = async (userId) => {
  const response = await API.get(`/users/${userId}/notification-preferences`);
  return response.data;
};

export const updateNotificationPreferences = async (userId, updates) => {
  const response = await API.patch(`/users/${userId}/notification-preferences`, updates);
  return response.data;
};

export const deleteAccount = async (userId) => {
  const response = await API.post(`/users/${userId}/delete-account`, {
    user_id: userId,
    confirmed: true
  });
  return response.data;
};

export const updateFirstName = async (userId, firstName) => {
  const response = await API.patch(`/users/${userId}/first-name`, {
    first_name: firstName
  });
  return response.data;
};

// Price Freeze — fee-refund model (see migrations/add_price_freezes.sql).
// Only pins currently a deal are eligible; the backend enforces this
// and returns a 400 with a clear message otherwise, which callers
// should surface directly rather than retrying.
// paymentMethodId/saveCard are both optional (Aug 14, per Ali's
// report — Stripe wiring): omitted entirely, this hits the same
// backend path as before (falls back to 'mocked' if Stripe isn't
// configured yet, or reuses a saved card if this user already has
// one). Only needs to be passed when the payment step actually
// collected a fresh card.
// Duration tiers (Sep 8, per Ali's report) — durationHours defaults
// to 120 (the original 5-day tier) so any existing caller that
// doesn't pass it keeps working unchanged, matching the backend's own
// default. Not validated client-side beyond the default — the backend
// is the real source of truth on which tiers actually exist
// (DURATION_TIERS in routes/freezes.py), so a bad value here surfaces
// as a clear 400 from the API rather than being silently guessed at
// on the frontend.
export const createFreeze = async (pinId, userId, paymentMethodId = null, saveCard = false, durationHours = 120) => {
  const response = await API.post('/freezes', {
    pin_id: pinId,
    user_id: userId,
    payment_method_id: paymentMethodId,
    save_card: saveCard,
    duration_hours: durationHours,
  });
  return response.data;
};

export const getFreezes = (userId) => dedupeGet(`/freezes/${userId}`, async () => {
  const response = await API.get(`/freezes/${userId}`);
  return response.data;
});

// Price Watch checkout numbers (Phase 2, Sep 22): the fee and both
// result thresholds in the pin's own currency, all from one exchange
// rate. The watch created right after uses these exact amounts.
export const getWatchQuote = async (pinId, durationHours) => {
  const response = await API.get(`/freezes/quote/${pinId}`, { params: { duration_hours: durationHours } });
  return response.data;
};

// New (Aug 31, per Ali's report — Phase A of the pin-purchase build).
// Same optional-payment-method shape as createFreeze above: omitted
// entirely, this hits the same backend fallback path (mocked if
// Stripe isn't configured, or the user's saved card if they have one)
// — only pass a real paymentMethodId when this checkout step actually
// collected a fresh card. currency defaults to CAD, matching every
// other money-related call in this file.
export const purchasePinBundle = async (userId, currency = 'CAD', paymentMethodId = null) => {
  const response = await API.post('/pin-purchases', {
    user_id: userId,
    currency,
    payment_method_id: paymentMethodId,
  });
  return response.data;
};

// New (Aug 31, per Ali's report — Phase C, item #4). Fire-and-forget
// from Login.js's signUp success handler — a failed welcome email
// should never block or surface an error during signup itself, so
// this is deliberately not awaited with error-handling by its one
// caller.
export const sendWelcomeEmail = async (userId) => {
  const response = await API.post(`/users/${userId}/welcome-email`);
  return response.data;
};

// New (Sep 2, per Ali's report — Purchase History feature).
export const getPurchaseHistory = async (userId) => {
  const response = await API.get(`/users/${userId}/purchase-history`);
  return response.data;
};

// Only succeeds within the backend's FREEZE_CANCEL_WINDOW_HOURS (24h)
// of the freeze's started_at — the backend enforces this, this is just
// the call site. Errors (already resolved, window passed, wrong user)
// come back as a 400/403/404 with a real detail message to surface.
export const cancelFreeze = async (freezeId, userId) => {
  const response = await API.post(`/freezes/${freezeId}/cancel`, { user_id: userId });
  return response.data;
};

// Feeds the freeze bar chart — every price_check_log point recorded
// for this freeze's pin between when it started and now (or its
// resolution time, if already resolved).
export const getFreezePricePoints = async (freezeId) => {
  const response = await API.get(`/freezes/${freezeId}/price-points`);
  return response.data;
};

// Feeds the Deal Screen's new mini price-history chart — no freeze
// needed, works for any pin that's been checked at all.
export const getPinPricePoints = async (pinId) => {
  const response = await API.get(`/trips/${pinId}/price-points`);
  return response.data;
};

// The Stripe publishable key — safe to expose (see routes/payments.py's
// own comment on why), fetched at runtime rather than hardcoded so a
// test-to-live key switch is a backend-only change, no frontend
// redeploy needed.
// Destination photography (Aug 16, per Ali's report — design item 4).
// photo_url is null when nothing's available (Pexels not configured,
// no results for this destination, or a request error) — callers
// treat that the same as "no photo," falling back to the existing
// flat-color card, not as an error to catch.
export const getDestinationPhoto = async (destination) => {
  const response = await API.get(`/photos/${encodeURIComponent(destination)}`);
  return response.data;
};

export const getStripeConfig = async () => {
  const response = await API.get('/payments/config');
  return response.data;
};

export const getSavedPaymentMethod = async (userId) => {
  const response = await API.get(`/payments/saved-method/${userId}`);
  return response.data;
};

// consent must be explicitly true — matches the backend's own
// requirement (routes/payments.py rejects a false/missing consent).
export const savePaymentMethod = async (userId, paymentMethodId, consent) => {
  const response = await API.post('/payments/saved-method', {
    user_id: userId,
    payment_method_id: paymentMethodId,
    consent,
  });
  return response.data;
};

export const removeSavedPaymentMethod = async (userId) => {
  const response = await API.delete(`/payments/saved-method/${userId}`);
  return response.data;
};

// Proof-of-purchase evidence upload (Aug 25, per Ali's report — Phase A,
// part 2). The one file upload in this codebase so far — needs its own
// multipart Content-Type, overriding the JSON default the shared API
// instance otherwise sends everywhere else.
export const uploadFreezeEvidence = async (freezeId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await API.post(`/freeze-evidence/${freezeId}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

// New (Aug 26, per Ali's report) — lets FreezeDetailScreen check whether
// evidence was already submitted for this freeze, on every real page
// load rather than only within the same session the upload happened in.
// Returns { evidence: null } rather than a 404 when nothing's been
// submitted yet — the normal case for most freezes, not an error.
export const getFreezeEvidence = async (freezeId) => {
  const response = await API.get(`/freeze-evidence/by-freeze/${freezeId}`);
  return response.data;
};

// New (Aug 31, per Ali's report — Phase 4, pin-creation autocomplete).
// Proxies the backend's own proxy of Duffel's Places API — see
// routes/places.py for why this goes through our own backend rather
// than calling Duffel directly (keeping the API key server-side).
export const getPlaceSuggestions = async (query) => {
  const response = await API.get(`/places/suggestions`, { params: { query } });
  return response.data;
};
// "Track this fare instead" (Oct 2026) — flips a trip between Best value
// and Direct only. One switch per trip, never combined with Price Watch;
// the backend enforces both and returns a plain-language detail on refusal.
export const switchPinFare = async (pinId) => {
  const response = await API.post(`/trips/${pinId}/switch-fare`);
  return response.data;
};
